import { useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { getCookie } from "utils/v3/CookieUtil";
import ModalComponent from "../sharedComponents/ModalComponent";
import SessionTimerDialog from "./SessionTimerDialog";
import { useLogout } from "../../hooks/useLogout";
import { useAuth0 } from "@auth0/auth0-react";
import { refreshAccessTokenCookie } from "../../utils/v3/accessTokenRefresh";
import {
  parseSessionTimeoutOverride,
  SESSION_TIMEOUT_OVERRIDE_EVENT,
  sessionTimeoutOverridesEnabled
} from "../../utils/v3/sessionTimeoutOverride";

/** Total idle time before the member is logged out. */
const DEFAULT_IDLE_TIMEOUT_IN_MILLISECONDS = 1000 * 60 * 14;
/** How long before that the "stay logged in" prompt appears. */
const DEFAULT_PROMPT_BEFORE_IDLE_IN_MILLISECONDS = 1000 * 59;

const IdleTimer = () => {
  const DEBUG_LOGGING = false;

  const [timeouts, setTimeouts] = useState({
    idleTimeoutMs: DEFAULT_IDLE_TIMEOUT_IN_MILLISECONDS,
    promptBeforeIdleMs: DEFAULT_PROMPT_BEFORE_IDLE_IN_MILLISECONDS
  });
  const promptBeforeIdleRef = useRef(timeouts.promptBeforeIdleMs);
  promptBeforeIdleRef.current = timeouts.promptBeforeIdleMs;

  const [logoutModalIsOpen, setLogoutModalIsOpen] = useState(false);
  const [remainingTimeToAct, setRemainingTimeToAct] = useState(
    timeouts.promptBeforeIdleMs
  );
  const logoutFunction = useLogout();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Development-only: let e2e tests shorten the timeouts at runtime.
  useEffect(() => {
    if (!sessionTimeoutOverridesEnabled()) {
      return undefined;
    }
    const handleOverride = (event) => {
      const override = parseSessionTimeoutOverride(event.detail);
      if (override) {
        setTimeouts(override);
      }
    };
    window.addEventListener(SESSION_TIMEOUT_OVERRIDE_EVENT, handleOverride);
    return () => {
      window.removeEventListener(
        SESSION_TIMEOUT_OVERRIDE_EVENT,
        handleOverride
      );
    };
  }, []);

  useEffect(() => {
    setRemainingTimeToAct(getRemainingTime());
    const sessionCheckIntervalTimerId = setInterval(() => {
      const totalTimeLeft = getRemainingTime();
      setRemainingTimeToAct(totalTimeLeft);
      if (totalTimeLeft > promptBeforeIdleRef.current) {
        setLogoutModalIsOpen(false);
      }
    }, 1000);
    return () => {
      clearInterval(sessionCheckIntervalTimerId);
    };
    // eslint-disable-next-line
  }, []);

  /**
   * @description onActive
    Called when the user makes one of the tracked actions
   */
  const onAction = (e) => {
    if (DEBUG_LOGGING) {
      console.log("User action happened, resetting idle timer");
    }
    reset();
  };

  /**
   * @description onActive
    This function is executed once the modal has been displayed
    and the user clicks the "Stay Logged In" button. Staying logged in
    also forces a fresh access token so the session really is extended.
   */
  const onActive = (e) => {
    setLogoutModalIsOpen(false);
    if (DEBUG_LOGGING) {
      console.log("User wants to stay active");
    }
    reset();
    if (getCookie("accessToken")) {
      refreshAccessTokenCookie(getAccessTokenSilently, { force: true });
    }
  };

  /**
   * @description onIdle
    This function is called when the time interval has expired, modal has been shown,
    and user did not take action, so they will be logged out.
   */
  const onIdle = () => {
    if (isAuthenticated) {
      setLogoutModalIsOpen(false);
      if (DEBUG_LOGGING) {
        console.log("User has gone idle, logging out");
      }
      logoutHandler();
    }
  };

  /**
   * @description onPrompt
    Called when user has been inactive long enough to be prompted to stay logged in
    or get logged out.
   */
  const onPrompt = () => {
    if (getCookie("accessToken")) {
      setLogoutModalIsOpen(true);
    }
  };

  const logoutHandler = () => {
    logoutFunction();
  };

  /**
   * @description useIdleTimer
   useIdleTimer is the hook that we are using to be able to use the react-idle-timer service.
   Documentation reference: https://idletimer.dev/
   */

  const { reset, getRemainingTime } = useIdleTimer({
    onPrompt,
    onIdle,
    onActive,
    onAction,
    timeout: timeouts.idleTimeoutMs,
    promptBeforeIdle: timeouts.promptBeforeIdleMs,
    events: ["click", "scroll", "keydown"],
    immediateEvents: [],
    debounce: 0,
    throttle: 0,
    eventsThrottle: 200,
    element: document,
    startOnMount: true,
    startManually: false,
    stopOnIdle: false,
    crossTab: true,
    name: "idle-timer",
    syncTimers: 0,
    leaderElection: false
  });
  return (
    <>
      <ModalComponent
        open={logoutModalIsOpen}
        body={
          <SessionTimerDialog
            handleClose={onActive}
            logOut={logoutHandler}
            second={parseInt(remainingTimeToAct / 1000)}
          />
        }
      />
    </>
  );
};

export default IdleTimer;
